
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://oihdhlvacxrixrsafron.supabase.co";
export const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9paGRobHZhY3hyaXhyc2Fmcm9uIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDEzNzk0MzEsImV4cCI6MjA1Njk1NTQzMX0.MiHNjCGXsfQzB2bhnKIM8JeGwrwCAwMHoyD8bdiYvfU";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Request queue for race condition prevention
let requestQueue: Array<() => void> = [];
let handshakeInProgress = false;
let handshakePromise: Promise<boolean> | null = null;

// Import retry function (will be available after sessionHeartbeat is loaded)
async function retryHandshakeBeforeError(): Promise<boolean> {
    try {
        const { retryHandshakeBeforeError: retryFn } = await import('@/utils/auth/sessionHeartbeat');
        return await retryFn();
    } catch {
        return false;
    }
}

// Configure client with global fetch interceptor
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    global: {
        fetch: async (url, options) => {
            const executeRequest = async (retryCount = 0): Promise<Response> => {
                try {
                    const headers = new Headers(options?.headers);
                    const deviceId = typeof localStorage !== 'undefined' ? localStorage.getItem('device_binding_id') : null;
                    if (deviceId) {
                        const clientInfo = headers.get('x-client-info') || 'supabase-js';
                        headers.set('x-client-info', `${clientInfo};device-id=${deviceId}`);
                    }
                    const response = await fetch(url, { ...options, headers });

                    // Detect 403/401/406/400 errors (IP binding or session issues)
                    const isAuthError = [400, 401, 403, 406].includes(response.status);

                    // Don't retry Supabase's internal auth endpoints - let them handle their own errors
                    const urlStr = url.toString();
                    const isSupabaseAuthEndpoint = urlStr.includes('/auth/v1/');

                    if (isAuthError && retryCount < 3 && !isSupabaseAuthEndpoint) {
                        return handleRetry(retryCount);
                    }

                    return response;
                } catch (error) {
                    // Catch "Load failed" / Network errors common during IP switches
                    const isNetworkError = error instanceof TypeError || (error as any)?.message?.includes('Load failed') || (error as any)?.message?.includes('δικτύου');

                    if (isNetworkError && retryCount < 3) {
                        console.log(`📡 Network error detected during fetch. Triggering recovery...`);
                        return handleRetry(retryCount);
                    }
                    throw error;
                }
            };

            const handleRetry = async (retryCount: number): Promise<Response> => {
                // Skip retry for sensitive endpoints that handle their own auth:
                // - session-sync, logout: Our own auth endpoints
                // - /token, /auth: Supabase's internal auth endpoints (token refresh)
                // Intercepting these causes infinite loops when access tokens expire
                const urlStr = url.toString();
                const isAuthEndpoint = urlStr.includes('session-sync') ||
                    urlStr.includes('logout') ||
                    urlStr.includes('/auth/v1/token') ||
                    urlStr.includes('/auth/v1/');

                if (isAuthEndpoint) {
                    // Let Supabase handle its own auth - don't interfere
                    console.log(`⏭️ Skipping retry for auth endpoint: ${urlStr.split('?')[0]}`);
                    throw new Error('Auth error on auth endpoint - letting Supabase handle it');
                }

                console.log(`🛡️ Recovery initiated. Queueing handshake retry (${retryCount + 1}/3)...`);

                if (handshakeInProgress && handshakePromise) {
                    console.log(`⏳ Waiting for active handshake...`);
                    const success = await handshakePromise;
                    if (success) {
                        return executeRequest(retryCount + 1);
                    }
                    throw new Error('Queued recovery failed');
                }

                // Start handshake
                handshakeInProgress = true;
                handshakePromise = retryHandshakeBeforeError();

                const success = await handshakePromise;

                // Reset state
                handshakeInProgress = false;
                handshakePromise = null;

                if (success) {
                    // CIRCUIT BREAKER: Give DB time to propagate the refreshed session binding
                    // before retrying. Storage RLS policies check is_session_verified() which
                    // reads from session_ip_bindings - needs time to be consistent after handshake.
                    await new Promise(r => setTimeout(r, 500));
                    return executeRequest(retryCount + 1);
                }

                throw new Error('Recovery failed');
            };

            return executeRequest();
        }
    }
});

/**
 * Enhanced client factory that reuses the primary client 
 * but injects a security context header.
 * REUSES the existing auth session to avoid "Multiple GoTrueClient instances" warnings.
 */
export const getContextualClient = (context: string) => {
  const handler = {
    get(target: any, prop: string) {
      const val = target[prop];
      
      // Handle .from() and .rpc() - inject headers into the builder
      if (typeof val === 'function' && (prop === 'from' || prop === 'rpc')) {
        return (...args: any[]) => {
          const builder = val.apply(target, args);
          if (builder && builder.headers) {
             builder.headers['x-app-context'] = context;
          }
          return builder;
        };
      }

      // Handle .functions.invoke() - inject into the invoke call options
      if (prop === 'functions') {
        const functionsTarget = target.functions;
        return new Proxy(functionsTarget, {
          get(fTarget, fProp) {
            const fVal = (fTarget as any)[fProp];
            if (fProp === 'invoke' && typeof fVal === 'function') {
              return (slug: string, options: any = {}) => {
                const headers = options.headers || {};
                options.headers = { ...headers, 'x-app-context': context };
                return fVal.call(fTarget, slug, options);
              };
            }
            return fVal;
          }
        });
      }

      return val;
    }
  };
  return new Proxy(supabase, handler);
};


